import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, OverlayView, Polyline, Circle } from '@react-google-maps/api';
import { db, database } from './firebase';
import { collection, query, where, onSnapshot, QuerySnapshot } from 'firebase/firestore';
import { ref, onValue, off, DataSnapshot } from 'firebase/database';
import type { OrderData } from './types/order';
import { getSplitPaths, getDistance, calculatePathDistance } from './utils/mapUtils';
import { decode } from '@googlemaps/polyline-codec';

const containerStyle = {
  width: '100%',
  height: '100vh', // Full viewport height
  margin: 0,
  padding: 0
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.2090
};

const mapOptions: google.maps.MapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: [
    // Clean, low-ink grayscale map (similar to the screenshot)
    { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels', stylers: [{ visibility: 'off' }] },

    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f3f4f6' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e6edf5' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },

    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e5e7eb' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
    { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#f3f4f6' }] },

    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f9fafb' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d1d5db' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#4b5563' }] },

    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#e5e7eb' }] },
    { featureType: 'administrative', elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
  ],
};

// Hook for smooth animation
const useSmoothPosition = (targetPosition: { lat: number; lng: number } | null, duration: number = 2000) => {
  const [currentPosition, setCurrentPosition] = useState(targetPosition);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const startPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  
  // Keep track of the latest position to use as start point for animation
  // This avoids adding currentPosition to the effect dependency array
  const currentPositionRef = useRef(currentPosition);
  currentPositionRef.current = currentPosition;

  useEffect(() => {
    if (!targetPosition) return;
    
    // Use the ref to get the current position at the start of the new animation
    const startPos = currentPositionRef.current;

    // If we don't have a current position, just snap to target
    if (!startPos) {
      setCurrentPosition(targetPosition);
      return;
    }

    // Calculate distance between current and target
    const dist = getDistance(startPos.lat, startPos.lng, targetPosition.lat, targetPosition.lng);

    // If distance is greater than 50 meters, snap to target
    if (dist > 50) {
      setCurrentPosition(targetPosition);
      if (requestRef.current !== null) cancelAnimationFrame(requestRef.current);
      return;
    }

    // Start animation
    startPositionRef.current = startPos;
    startTimeRef.current = performance.now();
    
    const animate = (time: number) => {
      // Ensure refs are set
      if (startTimeRef.current === null || !startPositionRef.current) return;
      
      const elapsed = time - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Linear interpolation
      const lat = startPositionRef.current.lat + (targetPosition.lat - startPositionRef.current.lat) * progress;
      const lng = startPositionRef.current.lng + (targetPosition.lng - startPositionRef.current.lng) * progress;

      // Only animate if there's a meaningful distance change
      if (Math.abs(lat - targetPosition.lat) < 0.00001 && Math.abs(lng - targetPosition.lng) < 0.00001) {
          setCurrentPosition({ lat: targetPosition.lat, lng: targetPosition.lng });
          return;
      }

      setCurrentPosition({ lat, lng });

      if (progress < 1) {
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current !== null) cancelAnimationFrame(requestRef.current);
    };
  }, [targetPosition?.lat, targetPosition?.lng, duration]); // eslint-disable-line react-hooks/exhaustive-deps

  return currentPosition;
};



const RiderTracking: React.FC = () => {
  const perfStartRef = useRef<number>(performance.now());
  const hasLoggedMapLoadRef = useRef(false);
  const hasLoggedRouteRef = useRef(false);
  const hasLoggedFitBoundsRef = useRef(false);
  const [orderId, setOrderId] = useState<string>('');
  const [shipmentId, setShipmentId] = useState<string>('');
  const [trackingData, setTrackingData] = useState<OrderData | null>(null);
  
  // Target agent location (from DB)
  const [targetAgentLocation, setTargetAgentLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Smoothly animated agent location (slowly, set to 2000ms)
  const animatedAgentLocation = useSmoothPosition(targetAgentLocation, 2000); 

  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  
  // New state for split paths, distance and ETA countdown
  const [pastPath, setPastPath] = useState<google.maps.LatLng[]>([]);
  const [futurePath, setFuturePath] = useState<google.maps.LatLng[]>([]);
  const [distanceText, setDistanceText] = useState<string>('');
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  
  // New state from the API payload
  // const [routeWaypoints, setRouteWaypoints] = useState<any[]>([]);
  const [routeDestination, setRouteDestination] = useState<{lat: number, lng: number} | null>(null);

  const [routeRefreshTrigger, setRouteRefreshTrigger] = useState(0);

  // Poll Route API every 5 minutes exactly
  useEffect(() => {
    const interval = setInterval(() => {
      setRouteRefreshTrigger(prev => prev + 1);
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (totalSeconds: number) => {
    const m = Math.ceil(totalSeconds / 60);
    return m <= 1 ? '1 min' : `${m} mins`;
  };
  
  const [snappedAgentLocation, setSnappedAgentLocation] = useState<{ lat: number, lng: number } | null>(null);
  const [riderRotation, setRiderRotation] = useState<number>(0);



  const [riderWaypoint, setRiderWaypoint] = useState<{ lat: number, lng: number } | null>(null);
  const lastWaypointUpdateRef = useRef<number>(0);
  const lastApiCallTimeRef = useRef<number>(0);
  const totalRouteDistanceRef = useRef<number>(0);
  const totalRouteDurationRef = useRef<number>(0);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Map instance ref for imperative actions (e.g., center on rider)
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
  });

  useEffect(() => {
    if (isLoaded) {
      console.log("Google Maps API loaded successfully");
      console.log(`[perf] maps sdk loaded: ${Math.round(performance.now() - perfStartRef.current)}ms`);
    }
    if (loadError) {
      console.error("Google Maps API failed to load:", loadError);
    }
  }, [isLoaded, loadError]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const orderIdParam = queryParams.get('order_id');
    const shipmentIdParam = queryParams.get('shipment_id');

    if (orderIdParam && shipmentIdParam) {
      setOrderId(orderIdParam);
      setShipmentId(shipmentIdParam);
    } else {
      setError("Missing order_id or shipment_id in URL");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!orderId || !shipmentId) return;

    setLoading(true);
    setError(null);

    const ordersRef = collection(db, "orders");
    const q = query(ordersRef, where("order_id", "==", orderId));

    const unsubscribe = onSnapshot(q, (querySnapshot: QuerySnapshot) => {
      if (!querySnapshot.empty) {
        const matched = querySnapshot.docs.find(
          (d) => String((d.data() as OrderData).shipment_id) === String(shipmentId)
        );
        if (matched) {
          const data = matched.data() as OrderData;
          setTrackingData(data);
          if (!data.agent_id) {
            setLoading(false);
          }
        } else {
          setError("Invalid Shipment ID for this Order");
          setTrackingData(null);
          setLoading(false);
        }
      } else {
        setError("Order not found");
        setTrackingData(null);
        setLoading(false);
      }
    }, (err) => {
      console.error("Firestore Error:", err);
      setError("Error fetching order. Permission denied or network issue.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [orderId, shipmentId]);

  useEffect(() => {
    const status = (trackingData?.status ?? '').toLowerCase();
    const showMap = status === 'started' || status === 'arrived';

    if (isLoaded && trackingData?.order_id && trackingData?.shipment_id && showMap) {
      const now = Date.now();
      // Allow API call if 5 minutes minus some buffer (290000ms) has passed, making sure setInterval works smoothly.
      if (lastApiCallTimeRef.current !== 0 && (now - lastApiCallTimeRef.current < 290000)) {
        console.log("Skipping Route API call - within 5 minute threshold");
        return;
      }

      const fetchRoute = async () => {
        try {
          console.log("Calling External Route API...");
          lastApiCallTimeRef.current = now;
          
          const response = await fetch(`https://riders.plazza.in/api/v1/delivery/tasks/route?order_id=${trackingData.order_id}&shipment_id=${trackingData.shipment_id}`, {
            headers: { 'Accept': 'application/json' }
          });
          const data = await response.json();

          // Using the native array of coordinates returned by the API or decoding the polyline if missing
          const parsedPath = data.path ? data.path : (data.polyline ? decode(data.polyline).map(([lat, lng]) => ({
              lat,
              lng
          })) : []);

          if (data.success && parsedPath.length > 0) {
            console.log("Route API Response:", data);
            if (!hasLoggedRouteRef.current) {
              hasLoggedRouteRef.current = true;
              console.log(`[perf] route received: ${Math.round(performance.now() - perfStartRef.current)}ms`);
            }
            
            const firstLeg = data.route?.legs?.[0];
            const localizedDuration = firstLeg?.localizedValues?.duration?.text;
            const localizedDistance = firstLeg?.localizedValues?.distance?.text;

            // Mocking a DirectionsResult structure to keep the rest of the app working
            const mockResult: any = {
              routes: [{
                overview_path: parsedPath.map((p: any) => new google.maps.LatLng(p.lat, p.lng)),
                legs: [{
                  duration: { 
                    text: (data.duration_seconds !== undefined ? `${Math.round(data.duration_seconds/60)} mins` : null) || localizedDuration || `${Math.round((data.duration_seconds || 0)/60)} mins`, 
                    value: data.duration_seconds || 0 
                  },
                  distance: { 
                    text: data.distance_km || localizedDistance || `${data.distance_meters || 0} m`, 
                    value: data.distance_meters || 0 
                  }
                }]
              }]
            };

            setDirectionsResponse(mockResult);

            // Store totals for position-based recalculation
            const totalMeters = data.distance_meters || data.google_response?.distanceMeters || 0;
            const totalSecs = data.duration_seconds ?? (data.google_response?.duration ? parseInt(data.google_response.duration) : 0);
            totalRouteDistanceRef.current = totalMeters;
            totalRouteDurationRef.current = totalSecs;

            // Set initial values; these will be overwritten once rider position is known
            if (totalMeters > 0) {
              setDistanceText(totalMeters >= 1000 ? `${(totalMeters / 1000).toFixed(1)} km` : `${Math.round(totalMeters)} m`);
            } else if (data.distance_km) {
              setDistanceText(data.distance_km);
            }
            if (totalSecs > 0) {
              setRemainingSeconds(totalSecs);
            }

            // if (data.waypoints) {
            //    setRouteWaypoints(data.waypoints);
            // }
            if (data.destination_lat && data.destination_lng) {
               setRouteDestination({ lat: data.destination_lat, lng: data.destination_lng });
            }
          }
        } catch (err) {
          console.error("Error fetching route from external API:", err);
        }
      };

      fetchRoute();
    }
  }, [isLoaded, trackingData?.order_id, trackingData?.shipment_id, trackingData?.status, riderWaypoint, routeRefreshTrigger]);

  // Listen to Agent Location
  useEffect(() => {
    if (!trackingData?.agent_id) return;

    const agentRef = ref(database, `agents/${trackingData.agent_id}`);
    
    const handleValueChange = (snapshot: DataSnapshot) => {
      if (snapshot.exists()) {
        const agentData = snapshot.val();
        if (agentData.lat && agentData.lng) {
          setTargetAgentLocation({ lat: agentData.lat, lng: agentData.lng });
        }
      }
      setLoading(false);
    };

    onValue(agentRef, handleValueChange);

    return () => {
      off(agentRef, 'value', handleValueChange);
    };
  }, [trackingData?.agent_id]);

  // Update Split Paths when rider moves or route loads
  useEffect(() => {
    if (directionsResponse && animatedAgentLocation) {
      const fullPath = directionsResponse.routes[0].overview_path;
      const { past, future, distanceToPath, snappedPos, bearing } = getSplitPaths(fullPath, animatedAgentLocation);
      
      if (bearing !== undefined) {
        setRiderRotation(bearing);
      }
      
      const now = Date.now();
      // Reroute if distance > 200 meters, but prevent infinite API loops by waiting 5 minutes (300,000 ms)
      if (distanceToPath > 200 && (now - lastWaypointUpdateRef.current > 300000)) {
        lastWaypointUpdateRef.current = now;
        setRiderWaypoint({ lat: animatedAgentLocation.lat, lng: animatedAgentLocation.lng });
        setSnappedAgentLocation(animatedAgentLocation);
      } else {
        setPastPath(past);
        setFuturePath(future);
        setSnappedAgentLocation(snappedPos);

        // Recalculate remaining distance and ETA based on how much path is left
        if (totalRouteDistanceRef.current > 0 && future.length > 1) {
          const remainingMeters = calculatePathDistance(future);
          setDistanceText(remainingMeters >= 1000
            ? `${(remainingMeters / 1000).toFixed(1)} km`
            : `${Math.round(remainingMeters)} m`
          );
          const proportion = Math.min(remainingMeters / totalRouteDistanceRef.current, 1);
          setRemainingSeconds(Math.round(proportion * totalRouteDurationRef.current));
        }
      }
    }
  }, [directionsResponse, animatedAgentLocation]);

  const [initialBoundsSet, setInitialBoundsSet] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;
    if (initialBoundsSet) return;
    if (!directionsResponse) return;

    const route = directionsResponse.routes?.[0];
    if (!route?.overview_path?.length) return;

    const bounds = new google.maps.LatLngBounds();
    route.overview_path.forEach((point) => bounds.extend(point));
    if (targetAgentLocation) bounds.extend(targetAgentLocation);
    mapRef.current.fitBounds(bounds, 80);
    if (!hasLoggedFitBoundsRef.current) {
      hasLoggedFitBoundsRef.current = true;
      console.log(`[perf] initial fitBounds: ${Math.round(performance.now() - perfStartRef.current)}ms`);
    }
    setInitialBoundsSet(true);
  }, [directionsResponse, targetAgentLocation, initialBoundsSet]);

  const handleCenterOnRider = () => {
    if (mapRef.current && animatedAgentLocation && trackingData?.address_lat && trackingData?.address_lng) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(animatedAgentLocation);
      bounds.extend({ lat: trackingData.address_lat, lng: trackingData.address_lng });
      mapRef.current.fitBounds(bounds, 100);
    }
  };

  const handleFitRoute = () => {
    if (mapRef.current && directionsResponse) {
      const route = directionsResponse.routes[0];
      if (!route) return;
      const bounds = new google.maps.LatLngBounds();
      route.overview_path.forEach((point) => bounds.extend(point));
      if (animatedAgentLocation) {
        bounds.extend(new google.maps.LatLng(animatedAgentLocation.lat, animatedAgentLocation.lng));
      }
      mapRef.current.fitBounds(bounds, 80);
    }
  };

  if (loading && !error) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading tracking details...</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>{error}</div>;
  }

  const status = (trackingData?.status ?? '').toLowerCase();
  const showMap = status === 'started' || status === 'arrived';

  if (showMap) {
    return (
      <div className="tracking-container">
        {/* Floating Info Overlay */}
        {trackingData && (
          <div className="order-info-overlay">
            {status === 'arrived' ? (
              <>
                <p>🎉 <strong>Your order has arrived!</strong> It will be at your door shortly.</p>
                
              </>
            ) : (
              <>
                {distanceText && (
                  <p><strong>{distanceText}</strong> Away</p>
                )}
                {remainingSeconds !== null && (
                  <p>Arriving in <strong>{formatTime(remainingSeconds)}</strong></p>
                )}
              </>
            )}
          </div>
        )}

        {/* Route control buttons */}
        {/* <div className="route-controls">
          <button className="route-btn" onClick={handleCenterOnRider}>
            Center on rider
          </button>
          <button className="route-btn" onClick={handleFitRoute}>
            Full route
          </button>
        </div> */}

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={animatedAgentLocation || (trackingData?.store_lat ? { lat: trackingData.store_lat, lng: trackingData.store_lng! } : defaultCenter)}
            zoom={14}
            options={mapOptions}
            onLoad={(map) => {
              mapRef.current = map;
              if (!hasLoggedMapLoadRef.current) {
                hasLoggedMapLoadRef.current = true;
                console.log(`[perf] map onLoad: ${Math.round(performance.now() - perfStartRef.current)}ms`);
              }
            }}
          >
          {/* Removed DirectionsRenderer to avoid script error on mismatched structures. Map bounds handled manually and lines drawn via Polyline component */}

          {/* Past Route: Light Blue Dashed */}
          <Polyline
            path={pastPath}
            options={{
              strokeColor: "#cbd5e1", // Slate-300 (subtle)
              strokeOpacity: 0,
              icons: [{
                icon: {
                  path: 'M 0,-1 0,1',
                  strokeOpacity: 1,
                  scale: 4
                },
                offset: '0',
                repeat: '20px'
              }],
            }}
          />

          {/* Future Route: Blue Solid */}
          <Polyline
            path={futurePath}
            options={{
              strokeColor: "#64748b", // Slate-500
              strokeOpacity: 0.95,
              strokeWeight: 5
            }}
          />

          {/* Store Marker (Start) - Custom Circular Icon */}
          {trackingData?.store_lat && trackingData?.store_lng && (
            <OverlayView
              position={{ lat: trackingData.store_lat, lng: trackingData.store_lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundImage: 'url(/icon.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '2px solid white',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer'
                }}
                title="Store"
              />
            </OverlayView>
          )}

          {/* Waypoints array as additional drop locations — commented out to show a single destination point only */}
          {/* {routeWaypoints.map((wp, index) => (
             <Marker
                key={`wp-${index}`}
                position={{ lat: wp.lat, lng: wp.lng }}
                label={{
                  text: wp.customer_name ? wp.customer_name.substring(0, 2).toUpperCase() : `D${index+1}`,
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px"
                }}
             />
          ))} */}

          {/* Customer Marker (Final Destination) - Initials + 200m Radius Circle */}
          {(routeDestination || (trackingData?.address_lat && trackingData?.address_lng)) && (() => {
            const dest = routeDestination ? routeDestination : { lat: Number(trackingData!.address_lat), lng: Number(trackingData!.address_lng) };
            return (
              <>
                <Circle
                  center={dest}
                  radius={300}
                  options={{
                    strokeColor: "#64748b", // Slate-500
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: "#94a3b8", // Slate-400
                    fillOpacity: 0.15,
                  }}
                />
                <Marker
                  position={dest}
                  label={{
                    text: trackingData?.customer_name ? trackingData.customer_name.substring(0, 2).toUpperCase() : "CS",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px"
                  }}
                />
              </>
            );
          })()}

          {/* Animated Rider Marker with Pulse Effect */}
          {(snappedAgentLocation || animatedAgentLocation) && (
            <OverlayView
              position={snappedAgentLocation || animatedAgentLocation!}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="rider-marker" style={{ 
                transform: `translate(-50%, -50%) rotate(${riderRotation - 90}deg)`, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                transition: 'transform 0.3s ease-out'
              }}>
                <img src="/rider_icon.png" alt="Rider" style={{ width: '70px', height: 'auto' }} />
              </div>
            </OverlayView>
          )}
          </GoogleMap>
        ) : (
          <div>Loading Map...</div>
        )}
      </div>
    );
  }

  return (
    <div className="result-screen">
      <div className="result-card">
        <p className="result-order">Order: {orderId}</p>
        <p className={`result-status result-status--${status === 'completed' ? 'completed' : status === 'cancelled' ? 'cancelled' : 'other'}`}>
          {status === 'completed' ? 'Order delivered' : status === 'cancelled' ? 'Delivery attempt failed' : `Status: ${trackingData?.status || 'Unknown'}`}
        </p>
        {status === 'completed' && <p className="result-message">Thank you for your order.</p>}
        {status === 'cancelled' && <p className="result-message">Our rider tried to deliver, but unfortunately the attempt failed.</p>}
      </div>
    </div>
  );
};

export default RiderTracking;
